import ApplicationController from './application_controller';
import { Chart, Interaction, registerables } from "chart.js";
import Logger from '../lib/logger';
import { getThemeColor, getThemeColorWithOpacity, getThemeColors } from '../config/chart_colors';
// Register Chart.js components once at module level (idempotent)
Chart.register(...registerables);
// An interaction mode for dots over a line — the bids benchmark's circuit chart (2026-11-02) — that names each
// dot on its own: the dot under the pointer nearest it, within the dots' hit radius (all of them where they
// are drawn on one spot), else, anywhere in the plot, the line series' points at the pointer's category. A tooltip listing every point of a category covered the dots it
// named. Dots are the scatter series; a line's point at a category is its own index there (a dot's index is
// its place in its own data). A series the tooltip leaves out (tooltipHidden) is never picked. Charts choose
// it in their options: interaction: { mode: 'pointOrLine' }.
export const pointOrLine = (chart, event, options, useFinalPosition) => {
    const datasets = chart.data.datasets;
    const isDot = (datasetIndex) => datasets[datasetIndex]?.type === 'scatter';
    const shown = (datasetIndex) => !datasets[datasetIndex]?.tooltipHidden;
    const dots = Interaction.modes.nearest(chart, event, { ...options, intersect: true, axis: 'xy' }, useFinalPosition)
        .filter(item => isDot(item.datasetIndex) && shown(item.datasetIndex));
    if (dots.length > 0)
        return dots;
    const { left, right, top, bottom } = chart.chartArea;
    const x = event.x ?? Number.NaN;
    const y = event.y ?? Number.NaN;
    if (!(x >= left && x <= right && y >= top && y <= bottom))
        return [];
    const index = Math.round(chart.scales.x?.getValueForPixel(x) ?? Number.NaN);
    if (!(index >= 0 && index < (chart.data.labels?.length ?? 0)))
        return [];
    return datasets.flatMap((_dataset, datasetIndex) => {
        const element = chart.getDatasetMeta(datasetIndex).data[index];
        const line = !isDot(datasetIndex) && shown(datasetIndex) && chart.isDatasetVisible(datasetIndex);
        return line && element ? [{ element, datasetIndex, index }] : [];
    });
};
if (Interaction?.modes)
    Interaction.modes.pointOrLine = pointOrLine;
const DEFAULT_PALETTE_COLORS = ['primary', 'success', 'warning', 'info', 'danger'];
// The dataset keys a colour may sit in, alone or one per point (resolveThemeColors).
const DATASET_COLOR_KEYS = ['borderColor', 'backgroundColor', 'pointBorderColor', 'pointBackgroundColor'];
// A point's note wraps on lines of its own under the point's value (2026-11-02): at this many characters,
// fewer on a chart too narrow for them (about 7px a character, the tooltip's padding and key aside), never
// fewer than the minimum.
const NOTE_LINE_WIDTH = 56;
const NOTE_LINE_MIN = 28;
const CHART_STATUS_MAP = {
    'status-counts': { key: 'summary_counts', label: 'Order Counts' },
    'status-revenue': { key: 'summary_revenue', label: 'Revenue' },
    'sold-monthly': { key: 'sold', label: 'Sold Orders' },
    'delivered-monthly': { key: 'delivered', label: 'Delivered Orders' },
    'cancelled-monthly': { key: 'cancelled', label: 'Cancelled Orders' },
    'extended-monthly': { key: 'extended', label: 'Extended Orders' },
    'weeks-stats': { key: 'weeks_stats', label: 'Weeks' },
    'stages-stats': { key: 'stages_stats', label: 'Stages' },
    'states-stats': { key: 'states_stats', label: 'States' },
    'customers-stats': { key: 'customers_stats', label: 'Customers' },
    'end-users-stats': { key: 'end_users_stats', label: 'End Users' },
    'services-stats': { key: 'services_stats', label: 'Services' },
    'delivered-last-year': { key: 'delivered_last_year', label: 'Delivered Last Year' },
    'delivered-this-year': { key: 'delivered_this_year', label: 'Delivered This Year' },
    'sales-last-year': { key: 'sales_last_year', label: 'Sales Last Year' },
    'sales-this-year': { key: 'sales_this_year', label: 'Sales This Year' },
};
class default_1 extends ApplicationController {
    constructor() {
        super(...arguments);
        this._charts = [];
        this._resizeHandler = null;
        this.themeObserver = null;
    }
    connect() {
        super.connect();
        Logger.debug('Charts controller connected');
        this.addEventListener(window, 'resize', this.handleResize);
        this.showLoading();
        setTimeout(() => {
            Logger.debug('Attempting to initialize charts...');
            this.initializeCharts();
        }, 100);
        // A canvas bakes its colours in when drawn, so a theme toggle (theme_toggle_controller flips
        // documentElement's data-bs-theme) redraws the charts in the new theme's (2026-11-02).
        this.themeObserver = new MutationObserver(() => this.redraw());
        this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-bs-theme'] });
    }
    disconnect() {
        this.themeObserver?.disconnect();
        this.themeObserver = null;
        this.destroy();
        super.disconnect();
        this.loadingTargets?.forEach(target => target.classList.add('d-none'));
    }
    async initializeCharts() {
        this._charts = [];
        if (this.loadingTargets.length > 0) {
            this.loadingTargets.forEach(target => target.classList.add('d-none'));
        }
        if (this.errorTargets.length > 0) {
            this.errorTargets.forEach(target => target.classList.add('d-none'));
        }
        try {
            if (this.hasChartUrl) {
                await this.loadChartData();
            }
            else {
                this.canvasTargets.forEach((canvas, index) => {
                    this.createChart(canvas, index);
                });
            }
        }
        catch (error) {
            Logger.error('Failed to initialize charts', error);
            this.showError();
        }
    }
    async loadChartData() {
        try {
            const canvas = this.canvasTargets[0];
            const chartUrl = canvas?.dataset?.chartUrl || this.clickUrlValue;
            if (!chartUrl) {
                Logger.error('No chart URL found in canvas data attributes');
                return;
            }
            Logger.debug('Loading chart data from URL:', chartUrl);
            const response = await this.safeFetch(chartUrl);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            Logger.debug('Chart data loaded:', data);
            this.canvasTargets.forEach((canvas, index) => {
                try {
                    this.createChartWithData(canvas, data, index);
                }
                catch (error) {
                    Logger.error(`Failed to create chart for ${canvas.id}`, error);
                }
            });
        }
        catch (error) {
            Logger.error('Failed to load chart data', error);
            this.showError();
        }
    }
    createChartWithData(canvas, data, index) {
        Logger.debug(`Creating chart for ${canvas.id} with loaded data`);
        if (!canvas)
            return;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            Logger.error('Could not get 2D context for canvas', { canvasId: canvas.id });
            return;
        }
        const chartConfig = this.parseLoadedData(canvas, data, index);
        Logger.debug(`Chart config for ${canvas.id}`, { config: chartConfig });
        const mergedConfig = this.buildMergedConfig(chartConfig);
        const chart = new Chart(ctx, mergedConfig);
        this._charts.push(chart);
        if (this.clickUrlValue || canvas.dataset.chartUrl) {
            this.addClickHandler(canvas, chart, chartConfig);
        }
        Logger.debug(`Chart created successfully: ${canvas.id}`);
    }
    // A scatter point's title is its own category (2026-11-02): Chart.js titles it by the label at its place in
    // its own data, so a dot drawn at 2026Q4 read "2026Q3". Anything else keeps Chart.js's title, its label.
    formatTooltipTitle(items) {
        const item = items[0];
        if (!item)
            return '';
        const scale = item.chart?.scales?.x;
        if (item.dataset?.type === 'scatter' && scale && typeof item.parsed?.x === 'number') {
            return String(scale.getLabelForValue(item.parsed.x));
        }
        return item.label || '';
    }
    // A series may name itself apart in the tooltip (tooltipLabel, 2026-11-02), where its legend label says more
    // than every point needs; a point's note goes on lines of its own under the value, wrapped, so a long one
    // does not run off the chart.
    formatTooltipLabel(context) {
        let label = context.dataset?.tooltipLabel || context.dataset?.label || '';
        if (label) {
            label += ': ';
        }
        // Determine if this chart is revenue-related by checking the canvas ID
        const isRevenue = context.chart?.canvas?.id?.includes('revenue') || false;
        const moneyUnit = context.chart?.canvas?.dataset?.chartMoneyUnit;
        if (context.parsed?.y !== null && typeof context.parsed.y === 'number') {
            if (moneyUnit) {
                label += this.formatMoney(moneyUnit, context.parsed.y, 2, this.moneyLocale(context.chart?.canvas));
            }
            else if (isRevenue) {
                label += new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL'
                }).format(context.parsed.y);
            }
            else {
                label += context.parsed.y.toLocaleString('pt-BR');
            }
        }
        if (context.parsed?.y !== null && context.raw && typeof context.raw === 'object') {
            if (context.raw.count) {
                label += ` (${context.raw.count} services)`;
            }
            if (context.raw.revenue) {
                label += ` \u2022 R$ ${Number(context.raw.revenue).toLocaleString('pt-BR')}`;
            }
            // A point may name itself: the server writes the words, the chart only shows them.
            if (context.raw.note) {
                const fits = Math.floor(((context.chart?.width ?? 640) - 80) / 7);
                return [label, ...this.wrapNote(String(context.raw.note), Math.max(NOTE_LINE_MIN, Math.min(NOTE_LINE_WIDTH, fits)))];
            }
        }
        return label;
    }
    wrapNote(note, width) {
        return note.split(' ').reduce((lines, word) => {
            const last = lines.length - 1;
            if (last >= 0 && `${lines[last]} ${word}`.length <= width) {
                lines[last] = `${lines[last]} ${word}`;
            }
            else {
                lines.push(word);
            }
            return lines;
        }, []);
    }
    // The theme's chart chrome (2026-11-02), from CSS custom properties read off the canvas (core/_theme.scss):
    // ink for the legend and axis titles, muted ink for the ticks, the gridlines and the tooltip. The dark theme
    // sets them all; the light one only the tooltip's background, so light charts keep Chart.js's defaults.
    chartChrome(canvas) {
        const style = getComputedStyle(canvas instanceof Element ? canvas : document.documentElement);
        const read = (name) => style.getPropertyValue(name).trim();
        return {
            ink: read('--chart-ink'),
            muted: read('--chart-ink-muted'),
            grid: read('--chart-grid'),
            tooltipBg: read('--chart-tooltip-bg'),
            tooltipBorder: read('--chart-tooltip-border')
        };
    }
    // A dataset colour may name a CSS custom property — "var(--pricing-chart-blue)" — read off the canvas,
    // so the stylesheet picks each theme's step (2026-11-02); one nothing sets stays as written.
    resolveThemeColors(data, canvas) {
        const style = getComputedStyle(canvas);
        const resolve = (color) => {
            const name = typeof color === 'string' ? color.match(/^var\((--[\w-]+)\)$/)?.[1] : undefined;
            return (name && style.getPropertyValue(name).trim()) || color;
        };
        const datasets = Array.isArray(data?.datasets) ? data.datasets : [];
        datasets.forEach(dataset => {
            DATASET_COLOR_KEYS.forEach(key => {
                const value = dataset[key];
                if (value !== undefined)
                    dataset[key] = Array.isArray(value) ? value.map(resolve) : resolve(value);
            });
        });
    }
    // Draws the charts again from their sources, in the current theme.
    redraw() {
        Logger.debug('Redrawing charts for the theme', { controller: this.constructor.name });
        this.destroy();
        this.initializeCharts();
    }
    // A chart of money names its unit on the canvas (data-chart-money-unit="R$ ", the server's symbol), and
    // reads in the app's money format: "R$ 1,500.00" in its tooltips, whole units on its axis — or in the
    // language of the block it sits in, where one sets it (2026-11-01): "R$ 1.500,00" under lang="pt-BR".
    formatMoney(unit, value, digits = 2, locale = 'en-US') {
        return unit + value.toLocaleString(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits });
    }
    // The language of the block a chart's canvas sits in, from its closest lang attribute; the app's English
    // where nothing sets one.
    moneyLocale(canvas) {
        return canvas?.closest?.('[lang]')?.getAttribute('lang') || 'en-US';
    }
    formatAfterLabel(context) {
        if (context.chart?.config?.type === 'doughnut') {
            const total = context.dataset?.data?.reduce((a, b) => a + b, 0) || 0;
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `${percentage}% of total`;
        }
        return '';
    }
    parseLoadedData(canvas, data, _index) {
        const canvasId = canvas.id;
        if (canvasId === 'combined-monthly-counts') {
            return this.getCombinedChartConfig(data);
        }
        if (canvasId === 'combined-monthly-revenue') {
            return this.getCombinedRevenueChartConfig(data);
        }
        const chartType = canvas.dataset?.chartType || this.chartTypeValue || 'bar';
        const statusInfo = CHART_STATUS_MAP[canvasId];
        const statusKey = statusInfo?.key || 'delivered';
        const chartData = data[statusKey] || data;
        Logger.debug(`Status key for ${canvasId}: ${statusKey}, chartData:`, chartData);
        if (chartData?.datasets) {
            return {
                type: chartType,
                data: chartData,
                options: {
                    responsive: true,
                    maintainAspectRatio: this.maintainAspectRatioValue
                }
            };
        }
        return {
            type: chartType,
            data: {
                labels: chartData?.labels || [],
                datasets: [{
                        label: statusInfo?.label || 'Orders',
                        data: chartType === 'bar' ? this.getChartDataArray(chartData) : [],
                        backgroundColor: chartData?.colors || this.getDatasetColor(canvasId),
                        borderColor: chartData?.border_colors || this.getDatasetBorderColor(canvasId),
                        borderWidth: 2
                    }]
            },
            options: {
                responsive: true,
                maintainAspectRatio: this.maintainAspectRatioValue
            }
        };
    }
    getCombinedChartConfig(data) {
        const extract = (d) => Array.isArray(d?.labels) ? d.labels : [];
        const labels = extract(data.sold) ||
            extract(data.delivered) ||
            extract(data.cancelled) ||
            extract(data.extended) ||
            ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return {
            type: 'bar',
            data: {
                labels,
                datasets: [
                    { label: 'Sold', data: this.getChartDataArray(data.sold), backgroundColor: getThemeColorWithOpacity('primary', 0.5), borderColor: getThemeColor('primary'), borderWidth: 1 },
                    { label: 'Delivered', data: this.getChartDataArray(data.delivered), backgroundColor: getThemeColorWithOpacity('success', 0.5), borderColor: getThemeColor('success'), borderWidth: 1 },
                    { label: 'Cancelled', data: this.getChartDataArray(data.cancelled), backgroundColor: getThemeColorWithOpacity('danger', 0.5), borderColor: getThemeColor('danger'), borderWidth: 1 },
                    { label: 'Extended', data: this.getChartDataArray(data.extended), backgroundColor: getThemeColorWithOpacity('warning', 0.5), borderColor: getThemeColor('warning'), borderWidth: 1 }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: true, position: 'top' }, title: { display: true, text: 'Monthly Orders by Status', color: getThemeColor('dark') } },
                scales: { y: { beginAtZero: true } }
            }
        };
    }
    getCombinedRevenueChartConfig(data) {
        const extract = (d) => Array.isArray(d?.labels) ? d.labels : [];
        const labels = extract(data.sold) ||
            extract(data.delivered) ||
            extract(data.cancelled) ||
            extract(data.extended) ||
            ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return {
            type: 'bar',
            data: {
                labels,
                datasets: [
                    { label: 'Sold Revenue', data: this.getRevenueDataArray(data.sold), backgroundColor: getThemeColorWithOpacity('primary', 0.5), borderColor: getThemeColor('primary'), borderWidth: 1 },
                    { label: 'Delivered Revenue', data: this.getRevenueDataArray(data.delivered), backgroundColor: getThemeColorWithOpacity('success', 0.5), borderColor: getThemeColor('success'), borderWidth: 1 },
                    { label: 'Cancelled Revenue', data: this.getRevenueDataArray(data.cancelled), backgroundColor: getThemeColorWithOpacity('danger', 0.5), borderColor: getThemeColor('danger'), borderWidth: 1 },
                    { label: 'Extended Revenue', data: this.getRevenueDataArray(data.extended), backgroundColor: getThemeColorWithOpacity('warning', 0.5), borderColor: getThemeColor('warning'), borderWidth: 1 }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { display: true, position: 'top' },
                    title: { display: true, text: 'Monthly Orders by Revenue (MRC)', color: getThemeColor('dark') },
                    tooltip: {
                        callbacks: {
                            label: (ctx) => {
                                let label = ctx.dataset?.label || '';
                                if (label)
                                    label += ': ';
                                if (ctx.parsed?.y != null) {
                                    label += new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(ctx.parsed.y);
                                }
                                return label;
                            }
                        }
                    }
                },
                scales: { y: { beginAtZero: true, ticks: { callback: (value) => 'R$ ' + value.toLocaleString('pt-BR') } } }
            }
        };
    }
    getChartDataArray(chartData) {
        if (chartData?.counts && chartData.counts.length > 0)
            return chartData.counts;
        if (chartData?.revenues && chartData.revenues.length > 0)
            return chartData.revenues;
        return [];
    }
    getRevenueDataArray(chartData) {
        if (chartData?.revenues && chartData.revenues.length > 0)
            return chartData.revenues;
        if (chartData?.counts && chartData.counts.length > 0)
            return chartData.counts;
        return [];
    }
    getStatusKey(canvasId) {
        return CHART_STATUS_MAP[canvasId]?.key || 'delivered';
    }
    getDatasetLabel(canvasId) {
        return CHART_STATUS_MAP[canvasId]?.label || 'Orders';
    }
    getDatasetColor(canvasId) {
        if (this.customColorsValue) {
            try {
                const customColors = JSON.parse(this.customColorsValue);
                const colors = customColors[this.getStatusKey(canvasId)];
                if (colors)
                    return colors;
            }
            catch (error) {
                Logger.error('Failed to parse custom colors', error);
            }
        }
        return getThemeColors().success;
    }
    getDatasetBorderColor(canvasId) {
        if (this.customColorsValue) {
            try {
                const customColors = JSON.parse(this.customColorsValue);
                const colors = customColors[this.getStatusKey(canvasId)];
                if (colors)
                    return colors;
            }
            catch (error) {
                Logger.error('Failed to parse custom colors', error);
            }
        }
        return getThemeColors().success;
    }
    createChart(canvas, _index) {
        try {
            Logger.debug(`Attempting to create chart for canvas ID: ${canvas.id}`);
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                Logger.error('Could not get 2D context for canvas', { canvasId: canvas.id });
                return;
            }
            const chartConfig = this.getChartConfig(canvas);
            this.resolveThemeColors(chartConfig.data, canvas);
            Logger.debug(`Chart config for ${canvas.id}`, { config: chartConfig });
            const mergedConfig = this.buildMergedConfig(chartConfig);
            const chart = new Chart(ctx, mergedConfig);
            this._charts.push(chart);
            if (this.clickUrlValue || canvas.dataset.chartUrl) {
                this.addClickHandler(canvas, chart, chartConfig);
            }
            Logger.debug(`Chart created successfully: ${canvas.id}`);
        }
        catch (error) {
            Logger.error(`Failed to create chart for ${canvas.id}`, error);
            this.showError();
        }
    }
    // Shared builder used by both createChart and createChartWithData
    buildMergedConfig(chartConfig) {
        const themeColors = getThemeColors();
        const chrome = this.chartChrome(chartConfig?._canvas);
        const chartType = chartConfig?.type || 'bar';
        const hasDatasets = Array.isArray(chartConfig?.data?.datasets) &&
            chartConfig.data.datasets.length > 0;
        const baseOptions = {
            responsive: this.responsiveValue,
            maintainAspectRatio: this.maintainAspectRatioValue,
        };
        if (hasDatasets) {
            const datasets = chartConfig.data.datasets;
            const hasLabels = datasets.some(ds => 'label' in ds && ds.label);
            const showLegend = datasets.length > 1 || (hasLabels && chartType !== 'doughnut');
            // A series may stay out of the legend (legendHidden) or the tooltip (tooltipHidden), as the edges of a
            // band do that one entry or a note names (2026-11-02).
            const plugins = {
                legend: {
                    display: showLegend,
                    position: 'top',
                    labels: {
                        ...(chrome.ink && { color: chrome.ink }),
                        filter: (item, data) => !data.datasets?.[item.datasetIndex]?.legendHidden
                    }
                },
                tooltip: {
                    enabled: true,
                    filter: (item) => !item.dataset?.tooltipHidden,
                    backgroundColor: chrome.tooltipBg || themeColors.dark,
                    // Painted under each key before its colour: the tooltip's own, so a hollow point's key reads hollow.
                    multiKeyBackground: chrome.tooltipBg || themeColors.dark,
                    titleColor: '#fff',
                    bodyColor: '#fff',
                    borderColor: chrome.tooltipBorder || themeColors.light,
                    borderWidth: 1,
                    cornerRadius: chartType === 'doughnut' ? 4 : 8,
                    displayColors: true,
                    usePointStyle: true,
                    callbacks: {
                        title: (items) => this.formatTooltipTitle(items),
                        label: (ctx) => this.formatTooltipLabel(ctx),
                        afterLabel: (ctx) => this.formatAfterLabel(ctx)
                    }
                }
            };
            if (chartConfig?.options?.plugins?.title) {
                plugins.title = chartConfig.options.plugins.title;
            }
            baseOptions.plugins = plugins;
        }
        const defaultScales = this.getScalesConfig(chartType, chrome.grid);
        const configScales = (chartConfig?.options?.scales) || {};
        if (Object.keys(defaultScales).length > 0 || Object.keys(configScales).length > 0) {
            baseOptions.scales = { ...defaultScales, ...configScales };
        }
        const moneyUnit = chartConfig?._canvas?.dataset?.chartMoneyUnit;
        if (moneyUnit && baseOptions.scales?.y) {
            const y = baseOptions.scales.y;
            const locale = this.moneyLocale(chartConfig._canvas);
            baseOptions.scales.y = {
                ...y, ticks: { ...y.ticks, callback: (value) => this.formatMoney(moneyUnit, value, 0, locale) }
            };
        }
        if (baseOptions.scales) {
            baseOptions.scales = this.withScaleChrome(baseOptions.scales, chrome);
        }
        // Merge any extra options from chartConfig beyond the known keys
        if (chartConfig?.options) {
            const exclude = ['responsive', 'maintainAspectRatio', 'plugins', 'scales'];
            for (const key of Object.keys(chartConfig.options)) {
                if (!exclude.includes(key)) {
                    baseOptions[key] = chartConfig.options[key];
                }
            }
        }
        return { ...chartConfig, type: chartType, options: baseOptions };
    }
    getScalesConfig(chartType, grid = '') {
        if (!['bar', 'line'].includes(chartType))
            return {};
        const color = grid || 'rgba(0, 0, 0, 0.05)';
        return {
            x: { display: true, grid: { color } },
            y: { display: true, grid: { color }, beginAtZero: true }
        };
    }
    // Each axis in the theme's chrome where it sets one (2026-11-02): gridlines, muted tick labels and inked
    // titles. What a chart's own options give an axis wins.
    withScaleChrome(scales, chrome) {
        return Object.fromEntries(Object.entries(scales).map(([key, axis]) => [key, {
                ...axis,
                ...(chrome.grid && { grid: { color: chrome.grid, ...axis?.grid } }),
                ...(chrome.muted && { ticks: { color: chrome.muted, ...axis?.ticks } }),
                ...(chrome.ink && axis?.title && { title: { color: chrome.ink, ...axis.title } })
            }]));
    }
    getChartConfig(canvas) {
        const baseData = {
            type: canvas.dataset?.chartType || this.chartTypeValue || 'bar',
            data: { labels: [], datasets: [] },
            options: { responsive: true }
        };
        const chartId = canvas.id;
        const dataScript = document.querySelector(`script[data-chart-id="${chartId}"][data-chart-type="data"]`);
        const optionsScript = document.querySelector(`script[data-chart-id="${chartId}"][data-chart-type="options"]`);
        if (dataScript?.textContent) {
            try {
                const rawData = JSON.parse(dataScript.textContent);
                baseData.data = this.normalizeChartData(rawData, canvas.dataset?.chartType || 'bar');
                Logger.debug(`Chart data loaded for ${chartId}`, { data: baseData.data });
            }
            catch (e) {
                Logger.error(`Failed to parse chart data for ${chartId}`, e);
            }
        }
        else {
            Logger.warn(`No data script found for chart ${chartId}`);
        }
        if (optionsScript?.textContent) {
            try {
                const parsedOptions = JSON.parse(optionsScript.textContent);
                baseData.options = this.mergeChartOptions(baseData.options, parsedOptions);
                Logger.debug(`Chart options loaded for ${chartId}`);
            }
            catch (e) {
                Logger.error(`Failed to parse chart options for ${chartId}`, e);
            }
        }
        else {
            Logger.warn(`No options script found for chart ${chartId}`);
            baseData.options = this.mergeChartOptions(baseData.options, { responsive: this.responsiveValue });
        }
        baseData._canvas = canvas;
        return baseData;
    }
    mergeChartOptions(current, incoming) {
        const result = current ? { ...current } : {};
        result.responsive = incoming.responsive ?? this.responsiveValue ?? true;
        result.maintainAspectRatio = incoming.maintainAspectRatio ?? this.maintainAspectRatioValue;
        for (const key of Object.keys(incoming)) {
            if (!['responsive', 'maintainAspectRatio'].includes(key)) {
                result[key] = incoming[key];
            }
        }
        return result;
    }
    normalizeChartData(data, chartType) {
        if (!data || typeof data !== 'object')
            return { labels: [], datasets: [] };
        if (('labels' in data) && ('datasets' in data))
            return data;
        const themeColors = getThemeColors();
        if (Array.isArray(data)) {
            const labels = data.map((item) => Array.isArray(item) ? String(item[0]) : String(item));
            const values = data.map((item) => Array.isArray(item) ? item[1] : 0);
            const colors = values.map((_, i) => themeColors[DEFAULT_PALETTE_COLORS[i % DEFAULT_PALETTE_COLORS.length]]);
            return {
                labels,
                datasets: [{
                        label: chartType === 'doughnut' ? 'Data' : 'Values',
                        data: values,
                        backgroundColor: colors,
                        borderColor: colors,
                        borderWidth: 2
                    }]
            };
        }
        const labels = Object.keys(data);
        const values = Object.values(data);
        const colors = values.map((_, i) => themeColors[DEFAULT_PALETTE_COLORS[i % DEFAULT_PALETTE_COLORS.length]]);
        return {
            labels,
            datasets: [{
                    label: 'Data',
                    data: values,
                    backgroundColor: colors,
                    borderColor: colors,
                    borderWidth: 2
                }]
        };
    }
    addClickHandler(canvas, chart, config) {
        const clickUrl = config?._canvas?.dataset?.chartUrl || this.clickUrlValue;
        if (!clickUrl)
            return;
        canvas.style.cursor = 'pointer';
        const handleClick = (event) => {
            const activePoints = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, false);
            if (activePoints.length > 0) {
                const firstPoint = activePoints[0];
                const datasets = config.data?.datasets;
                if (datasets) {
                    const dataset = datasets[firstPoint.datasetIndex];
                    // State-based click navigation
                    if (dataset?.state?.[firstPoint.index]) {
                        window.location.href = `${clickUrl}?q%5Bstate_id_eq%5D=${dataset.state[firstPoint.index]}`;
                        return;
                    }
                    // Month-based click navigation
                    if (dataset?.month?.[firstPoint.index]) {
                        const month = String(dataset.month[firstPoint.index]).padStart(2, '0');
                        const year = dataset.year?.[firstPoint.index] || new Date().getFullYear();
                        const startMonth = `${year}${month}01`;
                        const endMonth = `${year}${month}31`;
                        const filterColumn = config?._canvas?.dataset?.filterColumn || 'delivered_at';
                        window.open(`${clickUrl}?&q%5B${filterColumn}_gteq%5D=${startMonth}&q%5B${filterColumn}_lteq%5D=${endMonth}`, '_blank');
                        return;
                    }
                    // Week-based click navigation
                    if (dataset?.start_week?.[firstPoint.index]) {
                        let navigateUrl = `${clickUrl}?&q%5Bordered_at_gteq%5D=${dataset.start_week[firstPoint.index]}&q%5Bordered_at_lteq%5D=${dataset.end_week?.[firstPoint.index]}`;
                        if (config?._canvas?.dataset?.customerId) {
                            navigateUrl += `&q%5Bcustomer_id_eq%5D=${config._canvas.dataset.customerId}`;
                        }
                        window.open(navigateUrl);
                        return;
                    }
                }
                window.open(clickUrl, '_blank');
            }
        };
        this.addEventListener(canvas, 'click', handleClick);
    }
    showLoading() {
        if (this.loadingTargets.length > 0) {
            this.loadingTargets.forEach(target => target.classList.remove('d-none'));
        }
    }
    showError() {
        if (this.errorTargets.length > 0) {
            this.loadingTargets?.forEach(target => target.classList.add('d-none'));
            this.errorTargets.forEach(target => target.classList.remove('d-none'));
        }
        else {
            Logger.error('Chart rendering failed');
        }
    }
    hideError() {
        if (this.errorTargets.length > 0) {
            this.errorTargets.forEach(target => target.classList.add('d-none'));
        }
    }
    refresh() {
        this._charts.forEach(chart => chart.update());
    }
    destroy() {
        this._charts.forEach(chart => chart.destroy());
        this._charts = [];
    }
    addChart(canvasId) {
        const canvas = document.getElementById(canvasId);
        if (!canvas)
            return;
        try {
            this.createChart(canvas, 0);
        }
        catch (error) {
            Logger.error(`Failed to add chart for ${canvasId}`, error);
        }
    }
    updateChart(canvasId, newData) {
        const chartIndex = this._charts.findIndex(chart => chart.canvas.id === canvasId);
        if (chartIndex !== -1 && this._charts[chartIndex]) {
            const chart = this._charts[chartIndex];
            if (newData) {
                ;
                chart.data = this.normalizeChartData(newData, 'bar');
            }
            chart.update();
        }
        else {
            Logger.warn(`Chart with canvas ID "${canvasId}" not found`);
        }
    }
    getChartData(canvasId) {
        const chartIndex = this._charts.findIndex(chart => chart.canvas.id === canvasId);
        if (chartIndex !== -1 && this._charts[chartIndex]) {
            return this._charts[chartIndex].data;
        }
        Logger.warn(`Chart with canvas ID "${canvasId}" not found`);
        return null;
    }
    exportChart(canvasId, format = 'png') {
        const canvas = document.getElementById(canvasId);
        if (!canvas)
            return;
        try {
            const url = canvas.toDataURL(`image/${format}`);
            const link = document.createElement('a');
            link.download = `chart.${format}`;
            link.href = url;
            link.click();
        }
        catch (error) {
            Logger.error(`Failed to export chart ${canvasId}`, error);
        }
    }
    handleResize() {
        this._charts.forEach(chart => chart.resize());
    }
    get hasChartUrl() {
        return this.canvasTargets.some(canvas => canvas.dataset?.chartUrl);
    }
    // Backwards-compatibility alias for tests that reference `charts`
    get charts() { return this._charts; }
}
default_1.targets = ["canvas", "loading", "error"];
default_1.values = {
    clickUrl: String,
    chartType: { type: String, default: "bar" },
    responsive: { type: Boolean, default: true },
    maintainAspectRatio: { type: Boolean, default: false },
    customColors: { type: String, default: "" }
};
export default default_1;
